import React, { useContext, useState, useEffect, useRef } from "react";
import { CustomersContext } from "../providers/CustomersProvider";
import { useParams, useLocation, useHistory } from "react-router-dom";
import "../css/primaryForm.scss";
import { dateTimeFormatter } from "../helperFunctions/dateFormatters";

import { baseUrls } from "../popData";

import MailIcon from "@mui/icons-material/Mail";

import AddLocationIcon from "@mui/icons-material/AddLocation";

import JobStatusForm from "./JobStatusForm";
import ContactLog from "./ContactLog";
import DevPrimaryInspector from "./DevPrimaryInspector";
import { doc } from "firebase/firestore";
import { updatePrimary } from "../helperFunctions/primaryWrites";
import { findOnGoogleMaps, searchEmail } from "../helperFunctions/navigationHelpers";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import { SettingsContext } from "../providers";
import { Switch } from "@mui/material";
import { checkPendingDrafts, buildPendingDraftsUpdate } from "../helperFunctions/pendingDraftsCheck";

const PrimaryForm = ({ data, dbRef, primaryNumberText }) => {
  const customers = useContext(CustomersContext);
  const { vat, toggleVat, draftPollingEnabled } = useContext(SettingsContext);
  const params = useParams();
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    history.replace(location.pathname, { timesheet: { ...data } });
  }, [data, history, location.pathname]);

  const [formData, setFormData] = useState({});

  useEffect(() => {
    setFormData(data);
  }, [data]);

  // Checks any drafts created from this record against Gmail once per visit (not on every
  // formData update, which happens on any field edit) - a ref guard keyed on the record's
  // uid handles that, and naturally resets on a fresh mount (e.g. leaving to create another
  // draft and coming back), so a later visit does get a fresh check.
  const checkedPendingDraftsForUid = useRef(null);

  useEffect(() => {
    if (!draftPollingEnabled) return;

    let pendingDrafts = formData.pendingDrafts || [];
    if (!params.uid || !formData.hasPendingDrafts || pendingDrafts.length === 0) return;
    if (checkedPendingDraftsForUid.current === params.uid) return;
    checkedPendingDraftsForUid.current = params.uid;

    checkPendingDrafts(pendingDrafts).then((result) => {
      let update = buildPendingDraftsUpdate(formData, result);
      if (!update) return;

      updatePrimary(doc(dbRef, params.uid), update).catch((err) => {
        console.error("Failed to apply pending draft check results", err);
      });
    });
  }, [
    params.uid,
    formData.hasPendingDrafts,
    formData.pendingDrafts,
    formData.callLog,
    dbRef,
    draftPollingEnabled,
  ]);

  const setCustomerDefaults = () => {
    //Populate fields with customer defaults

    if (formData.customerUid) {
      let customer = findCustomer(formData.customerUid);

      updatePrimary(doc(dbRef, params.uid), {
        ...customer,
        uid: params.uid,
        customerUid: customer.uid,
      }).then(() => {
        setFormData((prevState) => {
          return {
            ...prevState,
            uid: params.uid,
            customerUid: customer.uid,
          };
        });
      });
    }
  };

  const findCustomer = (uid) => {
    if (!uid) return {};

    return customers.find((customer) => customer.uid === uid);
  };

  const customerHandler = (e) => {
    //Select customer and populate fields with empty values. When transferred over to access
    //it only uses the customer id. If any of the other fields are filled in, it will put them
    //in a different section.

    let value = e.target.value;
    let selectedCustomer = findCustomer(value);

    let { name = "", accessId = "" } = selectedCustomer;

    let defaultCustomerDetails = {
      city: "Glasgow",
      uid: params.uid,
      customerUid: value,
      accessId: accessId,
      address: "",
      address2: "",
      authorisedBy: "",
      email: "",
      flat: "",
      number: "",
      postcode: "",
      unit: "",
      name: name,
    };

    updatePrimary(doc(dbRef, params.uid), {
      ...defaultCustomerDetails,
    }).then(() => {
      setFormData((prevState) => {
        return {
          ...prevState,
          ...defaultCustomerDetails,
        };
      });
    });
  };

  const saveInput = (e) => {
    let name = e.target.name;
    let value = e.target.value;

    if (
      name === "paid" ||
      name === "sent" ||
      name === "cert" ||
      name === "subcontracted" ||
      name === "subcontractorPaid"
    ) {
      value = e.target.checked;
    } else {
      // trim inputs on save
      value = value.trim();
    }

    updatePrimary(doc(dbRef, `${params.uid}`), { [name]: value });
  };

  const inputHandler = (e) => {
    let name = e.target.name;
    let value = e.target.value;

    if (name === "paid" || name === "sent" || name === "cert") {
      value = e.target.checked;
    }

    if (name === "postcode") {
      value = value.toUpperCase();
    }

    setFormData({ ...formData, [name]: value });
  };

  // function googleMaps() {
  //   let baseUrl = baseUrls.searchGoogleMaps; //"https://www.google.com/maps/place/";

  //   let encodedAddress = encodeURI(
  //     `${formData?.address ?? ""} ${formData?.postcode ?? ""}`
  //   );

  //   window.open(baseUrl + encodedAddress, "_blank");
  // }

  // function searchEmail(e) {
  //   let searchType = e.currentTarget.dataset.searchtype || "";
  //   // console.log("E data 2 current", e.currentTarget.getAttribute("data-searchType"));

  //   let baseUrl = baseUrls.searchEmail; //"https://mail.google.com/mail/#search/";

  //   let encodedAddress = encodeURI(formData?.[searchType] || " ");

  //   // console.log("Encoded address", encodedAddress);

  //   // window.open(
  //   //   "mailto:Paul<paulyk1162@hotmail.co.uk>?subject=Hello there&body=This is the body"
  //   // );

  //   window.open(baseUrl + encodedAddress, "_blank");
  // }

  let primaryRefText = "Invoice";
  let secondaryRefText = "Secondary Ref";

  if (formData.primaryType === "quote") {
    primaryRefText = "Quote";
  } else if (formData.primaryType === "jobsheet") {
    primaryRefText = "Jobsheet";
  } else if (formData.primaryType === "restore") {
    primaryRefText = "Primary";
  }

  return (
    <div className="primaryForm">
      {formData?.vatMode === true && vat === false && (
        <div className="vatWarningBox">
          <p>
            <b>Warning:</b> this jobsheet uses vat but it is turned off in settings. Some functionality
            will be disabled.
          </p>
          <div className="formLine">
            <label htmlFor="vat">VAT (via Settings)</label>
            <Switch checked={vat} onChange={() => toggleVat(vat)} />
          </div>
        </div>
      )}
      {(formData?.vatMode === false || !formData?.vatMode) && vat === true && (
        <div className="vatWarningBox">
          <p>
            <b>Warning:</b> this jobsheet doesn't use vat but it is turned on in settings. Some
            functionality will be disabled.
          </p>
          <div className="formLine">
            <label htmlFor="vat">VAT (via Settings)</label>
            <Switch checked={vat} onChange={() => toggleVat(vat)} />
          </div>
        </div>
      )}
      <div className="generalJobSection section">
        {/* 
    formData obj
    handleStatus func
    inputHandler
    saveInput
    x status_of_jobsheets array
    x copyToClipboard func
    x showCopyMessage conditional
    */}

        <JobStatusForm
          formData={formData}
          inputHandler={inputHandler}
          saveInput={saveInput}
          dbRef={dbRef}
          params={params}
          // handleStatus={handleStatus}
          // copyToClipboard={copyToClipboard}
          // showCopyMessage={showCopyMessage}
        />

        <div className="refLine">
          <div className="formLine">
            <label htmlFor="refNumber">
              {/* {formData?.primaryType === "timesheet" ? "Invoice" : "Ref"} Number */}
              {primaryNumberText ?? primaryRefText} Number
            </label>
            <input
              id="refNumber"
              name="refNumber"
              type="text"
              value={formData.refNumber ?? ""}
              onChange={inputHandler}
              onBlur={saveInput}

              // onFocus={inputHandler}
              // onBlur={inputHandler}
            />
          </div>

          <div className="formLine">
            <label htmlFor="worksOrderNumber">{secondaryRefText} Number</label>

            <input
              id="worksOrderNumber"
              name="worksOrderNumber"
              type="text"
              value={formData.worksOrderNumber ?? ""}
              onChange={inputHandler}
              onBlur={saveInput}

              // onFocus={inputHandler}
              // onBlur={inputHandler}
            />
          </div>
        </div>

        <div className="jobReceivedLine">
          <div className="formLine jobReceivedDate">
            <label htmlFor="jobReceived">Job Received</label>
            <input
              id="jobReceived"
              name="jobReceived"
              type="date"
              value={formData.jobReceived ?? dateTimeFormatter(Date.now(), "d_full_variant")}
              onChange={inputHandler}
              onBlur={saveInput}
            />
          </div>

          <div className="checkboxes">
            <div className="formLine">
              <label htmlFor="paid">Paid</label>
              <input
                id="paid"
                name="paid"
                type="checkbox"
                checked={formData.paid ?? false}
                value={formData.paid ?? false}
                onChange={saveInput}
                // onBlur={saveInput}
              />
            </div>
            <div className="formLine">
              <label htmlFor="sent">Sent</label>
              <input
                id="sent"
                name="sent"
                type="checkbox"
                checked={formData.sent ?? false}
                value={formData.sent ?? false}
                onChange={saveInput}
                // onBlur={saveInput}
              />
            </div>

            <div className="formLine">
              <label htmlFor="cert">Cert</label>
              <input
                id="cert"
                name="cert"
                type="checkbox"
                checked={formData.cert ?? false}
                value={formData.cert ?? false}
                onChange={saveInput}
                // onBlur={saveInput}
              />
            </div>

            {formData.subcontracted && (
              <div className="formLine">
                <label htmlFor="subcontractorPaid">
                  <HowToRegIcon style={{ fontSize: 20 }} />
                </label>
                <input
                  id="subcontractorPaid"
                  name="subcontractorPaid"
                  type="checkbox"
                  checked={formData.subcontractorPaid ?? false}
                  value={formData.subcontractorPaid ?? false}
                  onChange={saveInput}
                  // onBlur={saveInput}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <hr />

      <ContactLog key={params.uid} formData={formData} dbRef={dbRef} params={params} />

      <div className="customerDetailsSection section">
        <h3 className="heading">Customer Details</h3>

        <div className="formLine">
          <label htmlFor="customerUid">Customer</label>
          <div className="customerLine">
            <select
              value={formData.customerUid}
              onChange={customerHandler}
              name="customerUid"
              id="customerUid"
            >
              <option value="">Customer</option>
              {customers.map(({ uid, name, accessId }) => {
                return (
                  <option key={uid} value={uid}>
                    {name}
                  </option>
                );
              })}
            </select>
            <button onClick={setCustomerDefaults}>Default</button>
          </div>
        </div>

        <div className="formLine">
          <label htmlFor="authorisedBy">
            Name
            <button
              data-searchtype="authorisedBy"
              name="name"
              className="emailSearch"
              onClick={(e) => searchEmail(e, formData?.authorisedBy)}
            >
              <MailIcon className="icon" />
            </button>
          </label>
          <input
            id="authorisedBy"
            name="authorisedBy"
            autoComplete="off"
            type="text"
            value={formData.authorisedBy ?? ""}
            onChange={inputHandler}
            onBlur={saveInput}

            // onChange={inputHandler}
            // onFocus={inputHandler}
            // onBlur={inputHandler}
          />
        </div>

        <div className="formLine">
          <label htmlFor="number">Number</label>
          <input
            id="number"
            name="number"
            type="text"
            autoComplete="off"
            value={formData.number ?? ""}
            onChange={inputHandler}
            onBlur={saveInput}

            // onFocus={inputHandler}
            // onBlur={inputHandler}
          />
        </div>

        <div className="formLine">
          <label htmlFor="email">
            Email{" "}
            <button
              data-searchtype="email"
              name="email"
              className="emailSearch"
              onClick={(e) => searchEmail(e, formData?.email)}
            >
              <MailIcon className="icon" />
            </button>
          </label>
          <input
            id="email"
            name="email"
            autoComplete="off"
            type="text"
            value={formData.email ?? ""}
            onChange={inputHandler}
            onBlur={saveInput}

            // onFocus={inputHandler}
            // onBlur={inputHandler}
          />
        </div>
      </div>
      <hr />

      <hr />

      <div className="addressSection section">
        <h3 className="heading">
          Address{" "}
          <button onClick={() => findOnGoogleMaps(formData?.address, formData?.postcode)}>
            <AddLocationIcon className="address-icon" />
          </button>
        </h3>

        <div className="formLine">
          <label htmlFor="address">Line 1</label>
          <input
            id="address"
            name="address"
            autoComplete="off"
            type="text"
            value={formData.address ?? ""}
            onChange={inputHandler}
            onBlur={saveInput}

            // onFocus={inputHandler}
            // onBlur={inputHandler}
          />
        </div>

        <div className="formLine">
          <label htmlFor="address2">Line 2</label>
          <input
            id="address2"
            name="address2"
            type="text"
            autoComplete="off"
            value={formData.address2 ?? ""}
            onChange={inputHandler}
            onBlur={saveInput}

            // onFocus={inputHandler}
            // onBlur={inputHandler}
          />
        </div>

        <div className="formLine">
          <label htmlFor="city">City</label>
          <input
            id="city"
            name="city"
            type="text"
            autoComplete="off"
            value={formData.city ?? ""}
            onChange={inputHandler}
            onBlur={saveInput}

            // onFocus={inputHandler}
            // onBlur={inputHandler}
          />
        </div>

        <div className="formLine">
          <label htmlFor="postcode">Post code</label>
          <input
            id="postcode"
            name="postcode"
            type="text"
            autoComplete="off"
            value={formData.postcode ?? ""}
            onChange={inputHandler}
            onBlur={saveInput}

            // onFocus={inputHandler}
            // onBlur={inputHandler}
          />
        </div>

        <div className="formLine">
          <label htmlFor="flat">Flat</label>
          <input
            id="flat"
            name="flat"
            type="text"
            autoComplete="off"
            value={formData.flat ?? ""}
            onChange={inputHandler}
            onBlur={saveInput}

            // onFocus={inputHandler}
            // onBlur={inputHandler}
          />
        </div>

        <div className="formLine">
          <label htmlFor="unit">Unit</label>
          <input
            id="unit"
            name="unit"
            autoComplete="off"
            type="text"
            value={formData.unit ?? ""}
            onChange={inputHandler}
            onBlur={saveInput}

            // onFocus={inputHandler}
            // onBlur={inputHandler}
          />
        </div>
      </div>
      <hr />

      <hr />

      <div className="jobDetailsSection section">
        <h3 className="heading">Job Details</h3>

        <div className="formLine">
          <label htmlFor="jobStart">Job Start</label>
          <input
            id="jobStart"
            name="jobStart"
            type="date"
            value={formData.jobStart ?? ""}
            onChange={inputHandler}
            onBlur={saveInput}
          />
        </div>

        <div className="formLine">
          <label htmlFor="jobEnd">Job End</label>
          <input
            id="jobEnd"
            name="jobEnd"
            type="date"
            value={formData.jobEnd ?? ""}
            onChange={inputHandler}
            onBlur={saveInput}
          />
        </div>

        <div className="formLine">
          <label htmlFor="jobDetails">Job Details</label>
          <textarea
            id="jobDetails"
            name="jobDetails"
            rows={2}
            value={formData.jobDetails ?? ""}
            onChange={inputHandler}
            onBlur={saveInput}

            // value={formData.jobDetails ?? ""}
            // onFocus={inputHandler}
            // onBlur={inputHandler}
          />
        </div>
      </div>

      <hr />

      <DevPrimaryInspector data={formData} />
    </div>
  );
};

export default React.memo(PrimaryForm);

// const setCustomerDefaultsOriginal = () => {
//   //Populate fields with customer defaults

//   if (formData.customerUid) {
//     let customer = findCustomer(formData.customerUid);

//     let {
//       accessId = "",
//       address = "",
//       address2 = "",
//       address3 = "",
//       jobLocation = "",
//       jobLocation2 = "",
//       jobLocation3 = "",
//       jobLocationPostcode = "",
//       jobLocationFlat = "",
//       jobLocationCity = "",
//       status = "",
//       authorisedBy = "",
//       city = "Glasgow",
//       email = "",
//       flat = "",
//       name = "",
//       homeNumber = "",
//       number = "",
//       postcode = "",
//       unit = "",
//       uid,
//     } = customer;

//     dbRef
//       .doc(params.uid)
//       .update({
//         accessId: accessId,
//         address,
//         address2,
//         authorisedBy,
//         city,
//         email,
//         status,
//         flat,
//         name,
//         number,
//         postcode,
//         unit,
//         uid: params.uid,
//         customerUid: uid,
//       })
//       .then(() => {
//         setFormData((prevState) => {
//           return {
//             ...prevState,
//             accessId: accessId,
//             address,
//             address2,
//             authorisedBy,
//             city,
//             email,
//             flat,
//             name,
//             number,
//             postcode,
//             unit,
//             uid: params.uid,
//             customerUid: uid,
//           };
//         });
//       });
//   }
// };

// {
/* <div className="statusLine">
  <div className="formLine">
    <select
      name="status"
      id="status"
      className="status-field"
      style={{
        ...colourScheme[formData?.status],
      }}
      onChange={handleStatus}
      value={formData?.status ?? ""}
    >
      {status_of_jobsheets.map((status) => {
        return (
          <option key={status.key} value={status.value}>
            {status.name}
          </option>
        );
      })}
    </select>
  </div>
</div>; */
// }
//
// {(formData?.status === "booked-in" ||
// formData?.status === "completed" ||
// formData?.status === "arrange-date") && (
// <>
//   <div className="formLine bookedInLine">
//     <label htmlFor="bookedInDateTime">Booked In</label>
//     <input
//       className="bookedInDateTime"
//       name="bookedInDateTime"
//       id="bookedInDateTime"
//       type="datetime-local"
//       value={formData?.bookedInDateTime ?? ""}
//       onChange={inputHandler}
//       onBlur={saveInput}
//     />
//     {/* <input
//     className="estimatedTime"
//     type="number"
//     name="bookedInTime"
//     id="bookedInTime"
//     placeholder="estimated time"
//   /> */}
//   </div>
//   <div className="copyButtons">
//     {/* <button
//       className="visitConfirmationCopy"
//       onClick={() => copyToClipboard("CUSTOMER_ADDRESS")}
//       disabled={showCopyMessage}
//     >
//       Address
//     </button> */}
//     <button
//       className="visitConfirmationCopy"
//       onClick={() => copyToClipboard("VISIT_CONFIRMATION")}
//       disabled={showCopyMessage}
//     >
//       Visit Confirm
//     </button>
//     <button
//       className="visitConfirmationCopy"
//       onClick={() => copyToClipboard("INVOICE_EICR")}
//       disabled={showCopyMessage}
//     >
//       Invoice (EICR)
//     </button>
//     <button
//       className="visitConfirmationCopy"
//       onClick={() => copyToClipboard("ALL_PAID_EICR")}
//       disabled={showCopyMessage}
//     >
//       All Paid (EICR)
//     </button>
//     <button
//       className="visitConfirmationCopy"
//       onClick={() => copyToClipboard("ALL_PAID_FA")}
//       disabled={showCopyMessage}
//     >
//       All Paid (Fire)
//     </button>
//     <button
//       className="visitConfirmationCopy"
//       onClick={() => copyToClipboard("INVOICE_FA")}
//       disabled={showCopyMessage}
//     >
//       Invoice (Fire)
//     </button>
//   </div>
// </>
// )}
